import { useEffect, useState } from 'react';
import api from '../services/api';

const cache = new Map<string, Promise<string>>();

/**
 * Resolves a media reference (`/api/media/{uuid}`, a bare uuid or an absolute URL)
 * to something an <img>/<video> can load. Previews need the Bearer token, so
 * protected files are fetched as a blob and served through an object URL
 * (same approach as VideoLessonPlayer / HotspotEditor, now in one place).
 * External URLs are returned unchanged; null while loading or on failure.
 */
export function useMediaObjectUrl(src: string | null | undefined): string | null {
    const [url, setUrl] = useState<string | null>(null);

    useEffect(() => {
        if (!src) {
            setUrl(null);
            return;
        }
        if (/^(https?:|blob:|data:)/i.test(src)) {
            setUrl(src);
            return;
        }
        const uuid = mediaUuidFrom(src);
        if (!uuid) {
            setUrl(src);
            return;
        }

        let cancelled = false;
        let promise = cache.get(uuid);
        if (!promise) {
            promise = api
                .get(`/media/preview/${uuid}`, { responseType: 'blob' })
                .then((res) => URL.createObjectURL(res.data as Blob));
            cache.set(uuid, promise);
            promise.catch(() => cache.delete(uuid));
        }
        promise.then((objectUrl) => {
            if (!cancelled) setUrl(objectUrl);
        }).catch(() => {
            if (!cancelled) setUrl(null);
        });

        return () => {
            cancelled = true;
        };
    }, [src]);

    return url;
}

/** Extracts the media uuid from `/api/media/{uuid}`, `/api/media/preview/{uuid}` or a bare uuid. */
export function mediaUuidFrom(src: string): string | null {
    const match = /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i.exec(src);
    return match ? match[1] : null;
}
